import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

export default function StayInTouch() {
  const { ref, inView } = useInView(0.1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.length > 500) return;
    setLoading(true);
    setError(null);

    try {
      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-[#EDF4EB] py-24 px-8 lg:px-16">
      <div
        ref={ref}
        className={`max-w-2xl mx-auto text-center transition-all duration-1000 ${
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <p className="text-[#C8973A] text-sm uppercase tracking-[0.3em] mb-3 font-semibold">
          Stay Connected
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#3A2A1A] font-bold mb-4">
          Say Hello
        </h2>
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="block w-12 h-px bg-[#C8973A]" />
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-mail-heart-line text-[#2C4A3B] text-lg" />
          </div>
          <span className="block w-12 h-px bg-[#C8973A]" />
        </div>
        <p
          className="text-[#7B6348] text-base mb-10 leading-relaxed"
          style={{ fontFamily: "'Nunito', sans-serif" }}
        >
          We&apos;re just getting started, but we&apos;d love to hear from you! Whether you want to learn
          more about the farm, be notified when we open, or simply say hello, drop us a note.
        </p>

        {submitted ? (
          <div className="bg-[#2C4A3B]/10 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#2C4A3B]/20 mx-auto mb-5">
              <i className="ri-check-line text-[#2C4A3B] text-3xl" />
            </div>
            <h3 className="font-serif text-2xl text-[#3A2A1A] font-bold mb-2">Bless you!</h3>
            <p className="text-[#7B6348] text-sm">
              We received your message and will get back to you soon. Have a wonderful, blessed day!
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-[#FFFEF9] rounded-2xl p-8 border border-[#EDE8DF] text-left"
          >
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-[#5C3D2E] text-sm font-semibold mb-2">Your Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full border border-[#DDD5C8] rounded-lg px-4 py-3 text-sm text-[#3A2A1A] bg-white focus:outline-none focus:border-[#2C4A3B] transition-colors"
                />
              </div>
              <div>
                <label className="block text-[#5C3D2E] text-sm font-semibold mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full border border-[#DDD5C8] rounded-lg px-4 py-3 text-sm text-[#3A2A1A] bg-white focus:outline-none focus:border-[#2C4A3B] transition-colors"
                />
              </div>
            </div>

            <div className="mb-2">
              <label className="block text-[#5C3D2E] text-sm font-semibold mb-2">Message</label>
              <textarea
                name="message"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us about yourself or ask us anything..."
                rows={4}
                maxLength={500}
                className="w-full border border-[#DDD5C8] rounded-lg px-4 py-3 text-sm text-[#3A2A1A] bg-white focus:outline-none focus:border-[#2C4A3B] transition-colors resize-none"
              />
              <p
                className={`text-xs mt-1 text-right ${
                  message.length > 480 ? 'text-[#C47A5A]' : 'text-[#8B7355]'
                }`}
              >
                {message.length}/500
              </p>
            </div>

            {message.length > 500 && (
              <p className="text-[#C47A5A] text-xs mb-3">
                Message exceeds 500 characters. Please shorten your message.
              </p>
            )}

            {error && (
              <p className="text-[#C47A5A] text-sm mb-3" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || message.length > 500}
              className="w-full py-4 bg-[#2C4A3B] hover:bg-[#3A5C4B] disabled:opacity-60 text-white text-sm font-semibold tracking-wider uppercase rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
